/**
 * S-53 報告書兼請求書 詳細・編集／S-55 助成金領収書 詳細・編集
 * 編集可：請求日／領収日付・住所・氏名・登録番号・明細備考・消費税・消費税率（消費税を変えると合計を再計算）
 */
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { FileDown, Save, Trash2 } from 'lucide-react';
import {
  Button,
  DateField,
  ErrorState,
  FormActions,
  FormField,
  FormGrid,
  Loading,
  NumberField,
  PageHeader,
  Panel,
  TextField,
  useConfirm,
  useToast,
} from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { applyApiErrors } from '@/lib/form';
import { toWareki } from '@/lib/wareki';
import { Money, Num } from '../common/components';
import { useDeleteStatement, useStatement, useUpdateStatement, type StatementDetail, type StatementType } from './api';

type FormValues = {
  date: string;
  claimantName: string;
  claimantAddress: string;
  invoiceRegistrationNo: string;
  taxAmount: number | null;
  taxRatePercent: number | null;
  lines: { id: number; note: string }[];
};

export function StatementDetailPage({ type }: { type: StatementType }) {
  const { id } = useParams();
  const q = useStatement(Number(id));
  if (q.isPending) return <Loading block />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return <StatementForm key={q.data.id + q.data.totalAmount} s={q.data} type={type} />;
}

function StatementForm({ s, type }: { s: StatementDetail; type: StatementType }) {
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { yearPath } = useFiscalYear();
  const update = useUpdateStatement(s.id);
  const del = useDeleteStatement();
  const isInvoice = type === '請求書';
  const listPath = yearPath(isInvoice ? 'invoices' : 'receipts');
  const {
    control,
    register,
    handleSubmit,
    watch,
    setError,
    formState: { isDirty, isSubmitting, errors },
  } = useForm<FormValues>({
    defaultValues: {
      date: (isInvoice ? s.billingDate : s.receiptDate) ?? '',
      claimantName: s.claimantName ?? '',
      claimantAddress: s.claimantAddress ?? '',
      invoiceRegistrationNo: s.invoiceRegistrationNo ?? '',
      taxAmount: s.taxAmount,
      taxRatePercent: s.taxRatePercent,
      lines: s.lines.map((l) => ({ id: l.id, note: l.note ?? '' })),
    },
  });
  const { fields } = useFieldArray({ control, name: 'lines' });
  const tax = watch('taxAmount') ?? 0;
  const total = s.taxableAmount + tax;

  const onSave = handleSubmit(async (v) => {
    try {
      await update.mutateAsync({
        ...(isInvoice ? { billingDate: v.date || null } : { receiptDate: v.date || null }),
        claimantName: v.claimantName || null,
        claimantAddress: v.claimantAddress || null,
        invoiceRegistrationNo: v.invoiceRegistrationNo || null,
        taxAmount: v.taxAmount ?? 0,
        taxRatePercent: v.taxRatePercent,
        lines: v.lines.map((l) => ({ id: l.id, note: l.note || null })),
      });
      toast.success(`${isInvoice ? '報告書兼請求書' : '助成金領収書'}を更新しました`);
    } catch (e) {
      if (!applyApiErrors(e, setError)) toast.error('保存できませんでした', errorMessage(e));
    }
  });

  const onDownload = async () => {
    if (isDirty && !(await confirm({ title: '未保存の変更があります', message: '保存前の内容で帳票を出力します。よろしいですか？', confirmLabel: '出力する' }))) return;
    try {
      const name = await downloadFile(`/expense-statements/${s.id}/report.xlsx`);
      toast.success('帳票を出力しました', name);
    } catch (e) {
      toast.error('帳票を出力できませんでした', errorMessage(e));
    }
  };

  const onDelete = async () => {
    if (!(await confirm({ title: '削除しますか？', message: 'この帳票データを削除します。この操作は取り消せません。', tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await del.mutateAsync(s.id);
      toast.success('削除しました');
      navigate(listPath);
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  return (
    <form onSubmit={onSave}>
      <PageHeader
        eyebrow={isInvoice ? '報告書兼請求書（別記3）' : '助成金領収書（別記4）'}
        title={`${s.vesselName}`}
        description={`調査日 ${s.periodFrom}〜${s.periodTo}（作成条件）　名簿No ${s.rosterNo ?? '—'}`}
        actions={
          <Button icon={<FileDown size={16} />} onClick={() => void onDownload()}>
            帳票出力
          </Button>
        }
      />

      <div className="bs-exp-stack">
        <div className="bs-exp-amount" aria-live="polite">
          <span className="bs-exp-amount__label">{isInvoice ? '請求金額' : '金'}</span>
          <Money value={total} className="bs-exp-amount__value" />
          <span className="bs-exp-amount__unit">円</span>
          <span className="bs-exp-muted" style={{ marginLeft: 'auto' }}>
            宛先：{s.addresseeName}
          </span>
        </div>

        <Panel title="帳票ヘッダ" description={isInvoice ? '翌月給油の場合、報告・請求日付は燃料費請求書の請求日付以降の日付にしてください。' : undefined}>
          <FormGrid columns={3}>
            <FormField label={isInvoice ? '請求日' : '領収日付'} hint={watch('date') ? toWareki(watch('date')) : undefined}>
              <DateField {...register('date')} />
            </FormField>
            <FormField label="氏名" error={errors.claimantName?.message}>
              <TextField {...register('claimantName')} />
            </FormField>
            <FormField label="登録番号" hint="適格請求書発行事業者の登録番号（T を除く 13 桁）" error={errors.invoiceRegistrationNo?.message}>
              <TextField prefix="T" {...register('invoiceRegistrationNo')} />
            </FormField>
            <FormField label="住所" span={3} error={errors.claimantAddress?.message}>
              <TextField width="full" {...register('claimantAddress')} />
            </FormField>
          </FormGrid>
        </Panel>

        <Panel title="明細" flush>
          <div className="bs-exp-sheet">
            <table aria-label="明細">
              <thead>
                <tr>
                  <th rowSpan={2}>調査年月日</th>
                  <th rowSpan={2}>就業者名</th>
                  <th colSpan={3}>賃金①</th>
                  <th colSpan={3}>用船料②</th>
                  <th colSpan={5}>燃料費③</th>
                  <th rowSpan={2}>備考</th>
                </tr>
                <tr>
                  <th>賃金</th>
                  <th>日数</th>
                  <th>金額</th>
                  <th>用船料</th>
                  <th>日数</th>
                  <th>金額</th>
                  <th>年月日</th>
                  <th>油種</th>
                  <th>数量</th>
                  <th>単価</th>
                  <th>金額</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f, i) => {
                  const l = s.lines[i]!;
                  return (
                    <tr key={f.id}>
                      <td className="bs-num">{l.surveyPeriodLabel}</td>
                      <td>{l.workerName}</td>
                      <td className="is-right"><Money value={l.wageUnit} /></td>
                      <td className="is-right"><Num value={l.wageDays} /></td>
                      <td className="is-right"><Money value={l.wageAmount} /></td>
                      <td className="is-right"><Money value={l.charterUnit} /></td>
                      <td className="is-right"><Num value={l.charterDays} /></td>
                      <td className="is-right"><Money value={l.charterAmount} /></td>
                      <td className="bs-num">{l.fuelDate ? toWareki(l.fuelDate) : ''}</td>
                      <td>{l.fuelType ?? ''}</td>
                      <td className="is-right"><Num value={l.fuelQuantity} /></td>
                      <td className="is-right"><Num value={l.fuelUnitPrice} /></td>
                      <td className="is-right"><Money value={l.fuelAmount} /></td>
                      <td>
                        <input className="bs-exp-cell-input is-text" aria-label={`${l.workerName} 備考`} {...register(`lines.${i}.note`)} />
                      </td>
                    </tr>
                  );
                })}
                <tr className="is-subtotal">
                  <td colSpan={2} className="is-center">小計</td>
                  <td />
                  <td className="is-right"><Num value={s.subtotals.wageDays} /></td>
                  <td className="is-right"><Money value={s.subtotals.wageAmount} /></td>
                  <td />
                  <td className="is-right"><Num value={s.subtotals.charterDays} /></td>
                  <td className="is-right"><Money value={s.subtotals.charterAmount} /></td>
                  <td colSpan={2} />
                  <td className="is-right"><Num value={s.subtotals.fuelQuantity} /></td>
                  <td />
                  <td className="is-right"><Money value={s.subtotals.fuelAmount} /></td>
                  <td />
                </tr>
                <tr className="is-total">
                  <td colSpan={2}>計（課税対象額）</td>
                  <td colSpan={12} className="is-right"><Money value={s.taxableAmount} unit="円" /></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="消費税・合計">
          <FormGrid columns={3}>
            <FormField label="消費税率（%）">
              <Controller control={control} name="taxRatePercent" render={({ field }) => <NumberField {...field} unit="%" decimals={2} width="xs" />} />
            </FormField>
            <FormField label="消費税" hint="自動計算値（L-11）。必要に応じて修正できます">
              <Controller control={control} name="taxAmount" render={({ field }) => <NumberField {...field} unit="円" width="sm" />} />
            </FormField>
            <FormField label="合計（税込み）">
              <Money value={total} unit="円" className="bs-exp-amount__value" />
            </FormField>
          </FormGrid>
        </Panel>
      </div>

      <FormActions>
        <Button variant="danger" icon={<Trash2 size={16} />} onClick={() => void onDelete()} loading={del.isPending}>
          削除
        </Button>
        <span style={{ flex: 1 }} />
        <Button onClick={() => navigate(listPath)}>一覧へ戻る</Button>
        <Button type="submit" variant="primary" icon={<Save size={16} />} loading={isSubmitting || update.isPending} disabled={!isDirty}>
          保存する
        </Button>
      </FormActions>
    </form>
  );
}
